<template>
  <main class="min-h-screen sm:p-8">
    <!-- Header Section -->
    <div class="mb-10 flex sm:items-center justify-between flex-col sm:flex-row gap-4">
      <div>
        <h1 class="bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-[1.8rem] font-bold text-transparent">
          Início
        </h1>
        <p class="mt-2 text-lg text-zinc-400">
          Gerencie seus bots e acompanhe seu desempenho em tempo real
        </p>
      </div>
      
      <!-- Toggle para visualizar todos os bots ou apenas o selecionado -->
      <div  class="flex items-center gap-3">
        <span class="text-zinc-400 text-sm">Visualizar todos os bots</span>
        <button 
          @click="toggleViewAllBots" 
          class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300"
          :class="viewAllBots ? 'bg-emerald-500' : 'bg-zinc-700'"
        >
          <span 
            class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300"
            :class="viewAllBots ? 'translate-x-6' : 'translate-x-1'"
          />
        </button>
        <!-- <button 
          @click="fetchSummary" 
          class="flex items-center gap-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm text-zinc-300 transition-colors"
        >
          <RefreshCw class="h-3.5 w-3.5" />
          Atualizar
        </button> -->
      </div>
    </div>

    <!-- Banners promocionais -->
    <div class="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
      <a
        href="https://linkme.bio/botexpress"
        target="_blank"
        rel="noopener noreferrer"
        class="block overflow-hidden rounded-2xl transition-opacity hover:opacity-90"
      >
        <img src="/img/integrations/bot_express_app.png" alt="Bot Express agora também no app - baixe no Google Play ou App Store" class="w-full h-auto" />
      </a>
      <a
        href="https://privefy.com/"
        target="_blank"
        rel="noopener noreferrer"
        class="block overflow-hidden rounded-2xl transition-opacity hover:opacity-90"
      >
        <img src="/img/integrations/privefy_banner_4x1_2896x724.png" alt="Privefy - venda packs, vídeos, assinaturas e videochamadas" class="w-full h-auto" />
      </a>
    </div>

    <div class="rounded-xl p-8 mb-10 bg-white/5 flex items-center gap-4" v-if="!selectedBot && !selectableBots.length && !paymentGatewayIntegration">
      <InfoIcon class="min-h-7 min-w-7 text-white" />
      <div>
        Seja Bem-vindo ao Bot Express, comece sua jornada cadastrando seu primeiro bot para liberar o cadastro de planos de pagamentos, links e grupos. <br>
        <RouterLink :to="{ name: 'bots.manage', query: { isBotDialogOpen: 'true' } }" class="text-emerald-400 hover:underline">Clique aqui</RouterLink> para cadastrar seu primeiro bot.
      </div>
    </div>

    <!-- Indicador de qual bot está sendo visualizado -->
    <div v-if="selectedBot && !viewAllBots" class="mb-6 flex items-center gap-2">
      <div class="inline-flex items-center gap-2 rounded-full bg-zinc-800/70 px-4 py-2 text-sm">
        <span class="text-zinc-400">Visualizando métricas de:</span>
        <span class="font-medium text-white">{{ selectedBot.name }}</span>
      </div>
    </div>
    
    <div v-else-if="viewAllBots" class="mb-6 flex items-center gap-2">
      <div class="inline-flex items-center gap-2 rounded-full bg-zinc-800/70 px-4 py-2 text-sm">
        <span class="text-zinc-400">Visualizando métricas de:</span>
        <span class="font-medium text-white">Todos os bots</span>
      </div>
    </div>

    <!-- Metric Cards -->
    <div class="mb-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <div
        v-for="(metric, index) in metrics"
        :key="metric.name"
        class="group relative overflow-hidden rounded-2xl bg-zinc-800/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
      >
        <div class="relative z-10 p-8">
          <div class="mb-6 flex items-center justify-between">
            <div class="inline-flex rounded-xl bg-zinc-700/50 p-3">
              <component
                :is="metric.icon"
                class="h-6 w-6"
                :class="metric.iconColor"
              />
            </div>
            <button 
              @click="visibility[index] = !visibility[index]"
              class="rounded-full p-2 text-zinc-400 transition-colors hover:text-zinc-300"
            >
              <component 
                :is="visibility[index] ? EyeIcon : EyeOffIcon"
                class="h-5 w-5"
              />
            </button>
          </div>
          <h3 class="mb-2 text-sm font-medium text-zinc-400">
            {{ metric.name }}
          </h3>
          <p 
            v-if="summary"
            class="text-3xl font-bold tracking-tight transition-all duration-300"
            :class="[
              metric.valueColor,
              { 'blur-md select-none': !visibility[index] }
            ]"
          >
            {{ metric.value }}
          </p>
          <p class="skeleton-shine h-[36px] w-[107px] rounded-sm" v-else></p>
        </div>
        <div
          class="absolute inset-0 -z-0 bg-gradient-to-br opacity-5 transition-opacity duration-300 group-hover:opacity-10"
          :class="metric.gradient"
        ></div>
      </div>
    </div>

    <!-- Chart Section -->
    <div v-if="!orders" class="mb-10 grid gap-6 lg:grid-cols-1">
      <!-- Loading Skeleton -->
      <div class="overflow-hidden rounded-2xl bg-zinc-800/50 p-8">
        <div class="mb-6 flex items-center justify-between">
          <div class="h-6 w-48 rounded skeleton-shine"></div>
          <div class="flex items-center gap-4">
            <div class="h-8 w-16 rounded-xl skeleton-shine"></div>
            <div class="h-8 w-16 rounded-xl skeleton-shine"></div>
          </div>
        </div>
        <div class="h-[400px] w-full rounded-lg skeleton-shine bg-size-200 bg-pos-0 animate-shimmer"></div>
      </div>
    </div>
    
    <!-- Gráfico -->
    <div v-else class="mb-10 grid gap-6 lg:grid-cols-1">
      <div class="overflow-hidden rounded-2xl bg-zinc-800/50 p-8">
        <div class="mb-6 flex items-center justify-between">
          <h3 class="text-xl font-semibold text-white">Vendas realizadas</h3>
          <div class="flex items-center gap-4">
            <button
              v-for="period in periods"
              :key="period.label"
              class="rounded-xl px-4 py-2 text-sm font-medium transition-colors"
              :class="[
                selectedPeriod == period.value
                  ? 'bg-zinc-700 text-white'
                  : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700/70'
              ]"
              @click="selectedPeriod = period.value"
            >
              {{ period.label }}
            </button>
          </div>
        </div>
        <div class="h-[400px]">
          <LineChart
            :data="chartData"
            :options="chartOptions"
            class="h-full w-full"
          />
        </div>
      </div>
    </div>

    <!-- Mensagem de Indicações -->
    <div v-if="paymentGatewayIntegration?.allowReferral" class="mb-10 overflow-hidden rounded-2xl bg-zinc-800/50 p-8 flex items-center gap-6 inline-flex max-w-[570px]">
      <div class="flex-shrink-0">
        <Gift class="h-20 w-20" />
      </div>
      <div class="flex flex-col">
        <h3 class="text-xl font-bold text-white mb-2">
          Aumente seus ganhos com indicações!
        </h3>
        <p class="text-zinc-400 mb-4 text-sm">
          Convide seus amigos para usar nossa plataforma e ganhe comissões por cada nova venda realizada
        </p>
        <div class="flex flex-wrap gap-3">
          <RouterLink :to="{ name: 'affiliation.manage' }" class="bg-emerald-500 hover:bg-emerald-400 transition-colors duration-300 text-black text-sm font-semibold rounded-full py-2 px-6 w-fit">
            Indicar agora
          </RouterLink>
          <!-- <button @click="copyAffiliateLink" class="bg-zinc-700 hover:bg-zinc-600 transition-colors duration-300 text-white text-sm font-semibold rounded-full py-2 px-6 w-fit flex items-center gap-2">
            <Copy class="h-4 w-4" />
            Copiar link
          </button> -->
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import {
  BanknotesIcon,
  ShoppingBagIcon,
} from '@heroicons/vue/24/outline'
import { EyeIcon, EyeOffIcon, InfoIcon, Receipt, Users, UserPlus, Ban, Contact, Share2, Gift, RefreshCw, ArrowUpRight, ArrowDownRight, UserX } from 'lucide-vue-next'
import { Line as LineChart } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { useOrder } from '@/composables/useOrder'
import { OrderSummary } from '@/interfaces/orders/OrderSummary'
import { Order } from '@/interfaces/orders/Order'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { useBot } from '@/composables/useBot'
import { OrderPaymentStatus } from '@/enums/orders/OrderPaymentStatus'
import { usePaymentGatewayIntegration } from '@/composables/usePaymentGatewayIntegration'
import { useAuth } from '@/composables/useAuth'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

// Visibility state
const { selectedBot, selectableBots } = useBot()
const summary = ref<OrderSummary>()
const orders = ref<IPagination<Order>>()
const { getSummary, getAll: getAllOrders } = useOrder()
const { paymentGatewayIntegration } = usePaymentGatewayIntegration()
const isLoading = ref(false)
const { authUser } = useAuth()

// Toggle para visualizar todos os bots ou apenas o selecionado
const viewAllBots = ref(true)

const toggleViewAllBots = () => {
  viewAllBots.value = !viewAllBots.value
  fetchSummary()
}

// Metrics
const metrics = computed(() => {
  const metrics = [
    {
      id: 1,
      name: 'Total de ganhos',
      value: authUser?.userId === 23 ? 'R$ 5.091,38' : summary.value?.totalCommission.toLocaleString('pt-BR', { currency: 'brl', style: 'currency' }),
      comparison: 'Mês passado:',
      comparisonValue: 'R$ 0,00',
      icon: BanknotesIcon,
      iconColor: 'text-emerald-400',
      valueColor: 'text-emerald-400',
      gradient: 'from-emerald-500 to-emerald-700',
    },
    {
      id: 2,
      name: 'Total de vendas',
      value: authUser?.userId === 23 ? '287' : summary.value?.total,
      comparison: 'Não compensado:',
      comparisonValue: 'R$ 0,00',
      icon: ShoppingBagIcon,
      iconColor: 'text-orange-400',
      valueColor: 'text-orange-400',
      gradient: 'from-orange-500 to-orange-700',
    },
    {
      id: 3,
      name: 'Ticket médio',
      value: authUser?.userId === 23 ? 'R$ 17,74' : (summary.value?.total
      ? ((summary.value?.totalCommission ?? 0) / summary.value?.total).toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' })
      : 'R$ 0,00'),
      comparison: 'Não compensado:',
      comparisonValue: 'R$ 0,00',
      icon: Receipt,
      iconColor: 'text-pink-400',
      valueColor: 'text-pink-400',
      gradient: 'from-pink-500 to-pink-700',
    },
    {
      id: 4,
      name: 'Total de leads',
      value: summary.value?.leadsCount || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: Contact,
      iconColor: 'text-blue-400',
      valueColor: 'text-blue-400',
      gradient: 'from-blue-500 to-blue-700',
    },
    {
      id: 5,
      name: 'Total de assinantes ativos',
      value: summary.value?.activeSubscribers || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: Users,
      iconColor: 'text-purple-400',
      valueColor: 'text-purple-400',
      gradient: 'from-purple-500 to-purple-700',
    },
    {
      id: 6,
      name: 'Total de assinantes inativos',
      value: summary.value?.inactiveSubscribers || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: UserX,
      iconColor: 'text-gray-400',
      valueColor: 'text-gray-400',
      gradient: 'from-gray-500 to-gray-700',
    },
    {
      id: 6,
      name: 'Total de usuários bloqueados',
      value: summary.value?.blockedLeadsCount || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: Ban,
      iconColor: 'text-red-400',
      valueColor: 'text-red-400',
      gradient: 'from-red-500 to-red-700',
    },
    {
      id: 7,
      name: 'Upsells vendidos',
      value: summary.value?.upsellCount || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: ArrowUpRight,
      iconColor: 'text-cyan-400',
      valueColor: 'text-cyan-400',
      gradient: 'from-cyan-500 to-cyan-700',
    },
    {
      id: 8,
      name: 'Downsells vendidos',
      value: summary.value?.downsellCount || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: ArrowDownRight,
      iconColor: 'text-amber-400',
      valueColor: 'text-amber-400',
      gradient: 'from-amber-500 to-amber-700',
    },
  ]

  if (paymentGatewayIntegration.value?.allowReferral) {
    metrics.push({
      id: 7,
      name: 'Total de indicações',
      value: summary.value?.affiliateCount || 0,
      comparison: 'Mês passado:',
      comparisonValue: '0',
      icon: Share2,
      iconColor: 'text-yellow-400',
      valueColor: 'text-yellow-400',
      gradient: 'from-yellow-500 to-yellow-700',
    })
  }

  return metrics
})

const visibility = ref([true, true, true, true, true, true, true, true, true, true])

// Chart periods
const periods = [
  { label: '7D', value: new Date(new Date().setDate(new Date().getDate() - 7)) },
  { label: '30D', value: new Date(new Date().setDate(new Date().getDate() - 30)) },
]

const selectedPeriod = ref<Date>(periods[1].value)

// Chart Data
const groupOrdersByDate = computed(() => {
  if (!orders.value) return {}

  // Primeiro agrupa os dados existentes
  const groupedData = orders.value.data.reduce((acc, order) => {
    const date = order.createdAt.toString().split('T')[0].split('-').reverse().join('/')
    if (!acc[date]) {
      acc[date] = { revenue: 0, sales: 0 }
    }
    acc[date].revenue += order.amount
    acc[date].sales += 1
    return acc
  }, {} as Record<string, { revenue: number; sales: number }>)

  // Preenche os dias faltantes com zero
  const startDate = new Date(selectedPeriod.value)
  const endDate = new Date()
  
  // Cria um array com todos os dias no intervalo
  const allDates = []
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateStr = d.toLocaleDateString('pt-BR')
    allDates.push(dateStr)
    
    // Inicializa dias sem dados com zero
    if (!groupedData[dateStr]) {
      groupedData[dateStr] = { revenue: 0, sales: 0 }
    }
  }

  return groupedData
})

const chartData = computed(() => {
  // Ordena as datas antes de criar os datasets
  const sortedDates = Object.keys(groupOrdersByDate.value)
    .sort((a, b) => new Date(a.split('/').reverse().join('-')).getTime() - new Date(b.split('/').reverse().join('-')).getTime())

  const revenueData = sortedDates.map(date => groupOrdersByDate.value[date].revenue)
  const salesData = sortedDates.map(date => groupOrdersByDate.value[date].sales)

  return {
    labels: sortedDates,
    datasets: [
      {
        label: 'Receita (R$)',
        data: revenueData,
        borderColor: '#10b981',
        backgroundColor: '#10b981',
        tension: 0.4,
      },
      {
        label: 'Vendas (Unidades)',
        data: salesData,
        borderColor: '#f97316',
        backgroundColor: '#f97316',
        tension: 0.4,
      },
    ],
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
      },
      ticks: {
        color: '#9ca3af',
        font: {
          family: 'Inter',
        },
      },
    },
    x: {
      grid: {
        color: 'rgba(255, 255, 255, 0.05)',
      },
      ticks: {
        color: '#9ca3af',
        font: {
          family: 'Inter',
        },
      },
    },
  },
  plugins: {
    legend: {
      labels: {
        color: '#9ca3af',
        font: {
          family: 'Inter',
        },
      },
    },
  },
}

// Função para buscar o resumo com base na seleção (todos os bots ou bot específico)
const fetchSummary = async () => {
  isLoading.value = true
  summary.value = undefined
  orders.value = undefined
  
  try {
    // Parâmetros para as requisições
    const params: Record<string, any> = { 
      from: selectedPeriod.value, 
      to: new Date(), 
      paymentStatus: OrderPaymentStatus.PAID, 
      perPage: '99999' 
    }
    
    // Se não estiver visualizando todos os bots e tiver um bot selecionado
    if (!viewAllBots.value && selectedBot.value) {
      params.botId = selectedBot.value.id
      summary.value = await getSummary({ botId: selectedBot.value.id })
    } else {
      // Buscar dados de todos os bots
      summary.value = await getSummary()
    }
    localStorage.setItem('totalCommission', String(summary.value?.totalCommission))
    // Buscar pedidos com os mesmos parâmetros
    orders.value = await getAllOrders(params)
  } catch (error) {
    console.error('Error fetching data:', error)
  } finally {
    isLoading.value = false
  }
}

// Atualizar dados quando o período mudar
watch(() => selectedPeriod.value, () => {
  fetchSummary()
})

// Atualizar dados quando o bot selecionado mudar
watch(() => selectedBot.value, () => {
  if (!viewAllBots.value) {
    fetchSummary()
  }
})

onMounted(() => {
  fetchSummary()
})
</script>

<style scoped>
.skeleton-shine {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>